import React, { useEffect } from 'react';
import { X } from 'lucide-react';
import { InvitationDetailResponse } from '@/services/invitationsService';

interface InvitationDetailModalProps {
   isOpen: boolean;
   onClose: () => void;
   invitationDetail: InvitationDetailResponse | null;
   loading: boolean;
}

const InvitationDetailModal: React.FC<InvitationDetailModalProps> = ({
   isOpen,
   onClose,
   invitationDetail,
   loading,
}) => {
   // Handle escape key press
   useEffect(() => {
      const handleEscape = (e: KeyboardEvent) => {
         if (e.key === 'Escape') {
            onClose();
         }
      };

      if (isOpen) {
         document.addEventListener('keydown', handleEscape);
         document.body.style.overflow = 'hidden';
      }

      return () => {
         document.removeEventListener('keydown', handleEscape);
         document.body.style.overflow = 'unset';
      };
   }, [isOpen, onClose]);

   const formatDate = (dateString: string | Date | undefined) => {
      if (!dateString) return 'N/A';
      return new Date(dateString).toLocaleDateString('en-US', {
         year: 'numeric',
         month: 'long',
         day: 'numeric',
         hour: '2-digit',
         minute: '2-digit',
      });
   };

   const getStatusColor = (status: string) => {
      switch (status?.toLowerCase()) {
         case 'accepted':
            return 'bg-green-100 text-green-800';
         case 'pending':
            return 'bg-yellow-100 text-yellow-800';
         case 'rejected':
            return 'bg-red-100 text-red-800';
         case 'invited':
            return 'bg-blue-100 text-blue-800';
         default:
            return 'bg-gray-100 text-gray-800';
      }
   };

   const getTypeIcon = (type: string) => {
      switch (type) {
         case 'album':
            return '📸';
         case 'event':
            return '🎉';
         case 'trip':
            return '✈️';
         default:
            return '📝';
      }
   };

   if (!isOpen) return null;

   return (
      <div className='fixed inset-0 z-50 overflow-y-auto'>
         {/* Backdrop */}
         <div
            className='fixed inset-0 bg-black bg-opacity-50 transition-opacity'
            onClick={onClose}
         />

         {/* Modal */}
         <div className='flex min-h-full items-center justify-center p-4'>
            <div className='relative w-full max-w-4xl transform overflow-hidden rounded-lg bg-white shadow-xl transition-all'>
               {loading ? (
                  <div className='flex items-center justify-center h-64 p-6'>
                     <div className='animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600'></div>
                  </div>
               ) : invitationDetail ? (
                  <>
                     {/* Header */}
                     <div className='flex items-center justify-between p-6 border-b border-gray-200'>
                        <div className='flex items-center space-x-3'>
                           <span className='text-2xl'>{getTypeIcon(invitationDetail.type)}</span>
                           <div>
                              <h3 className='text-lg font-medium leading-6 text-gray-900'>
                                 {invitationDetail.type.charAt(0).toUpperCase() +
                                    invitationDetail.type.slice(1)}{' '}
                                 Invitation Details
                              </h3>
                              <p className='text-sm text-gray-500'>
                                 Invitation ID: {invitationDetail.invitation.id}
                              </p>
                           </div>
                        </div>
                        <button
                           type='button'
                           className='rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'
                           onClick={onClose}
                        >
                           <span className='sr-only'>Close</span>
                           <X className='h-6 w-6' />
                        </button>
                     </div>

                     {/* Content */}
                     <div className='p-6 max-h-[70vh] overflow-y-auto'>
                        <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
                           {/* Left Column - Invitation Information */}
                           <div className='space-y-6'>
                              <div>
                                 <h4 className='text-sm font-medium text-gray-900 mb-3'>
                                    Invitation Information
                                 </h4>
                                 <div className='bg-gray-50 p-4 rounded-lg space-y-3'>
                                    <div className='flex justify-between'>
                                       <span className='text-sm text-gray-500'>Status:</span>
                                       <span
                                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                                             invitationDetail.invitation.status
                                          )}`}
                                       >
                                          {invitationDetail.invitation.status
                                             ?.charAt(0)
                                             .toUpperCase() +
                                             invitationDetail.invitation.status?.slice(1)}
                                       </span>
                                    </div>
                                    <div className='flex justify-between'>
                                       <span className='text-sm text-gray-500'>Method:</span>
                                       <span className='text-sm text-gray-900'>
                                          {invitationDetail.invitation.method}
                                       </span>
                                    </div>
                                    <div className='flex justify-between'>
                                       <span className='text-sm text-gray-500'>Role:</span>
                                       <span className='text-sm text-gray-900'>
                                          {invitationDetail.invitation.role}
                                       </span>
                                    </div>
                                    <div className='flex justify-between'>
                                       <span className='text-sm text-gray-500'>Created:</span>
                                       <span className='text-sm text-gray-900'>
                                          {formatDate(invitationDetail.invitation.createdAt)}
                                       </span>
                                    </div>
                                    {invitationDetail.invitation.expiresAt && (
                                       <div className='flex justify-between'>
                                          <span className='text-sm text-gray-500'>Expires:</span>
                                          <span className='text-sm text-gray-900'>
                                             {formatDate(invitationDetail.invitation.expiresAt)}
                                          </span>
                                       </div>
                                    )}
                                    {invitationDetail.invitation.acceptedAt && (
                                       <div className='flex justify-between'>
                                          <span className='text-sm text-gray-500'>Accepted:</span>
                                          <span className='text-sm text-gray-900'>
                                             {formatDate(invitationDetail.invitation.acceptedAt)}
                                          </span>
                                       </div>
                                    )}
                                 </div>
                              </div>

                              {/* Creator Information */}
                              {invitationDetail.creator && (
                                 <div>
                                    <h4 className='text-sm font-medium text-gray-900 mb-3'>
                                       Created By
                                    </h4>
                                    <div className='bg-gray-50 p-4 rounded-lg'>
                                       <div className='flex items-center space-x-3'>
                                          {invitationDetail.creator.profilePicture ? (
                                             <img
                                                className='h-10 w-10 rounded-full'
                                                src={invitationDetail.creator.profilePicture}
                                                alt={invitationDetail.creator.name}
                                             />
                                          ) : (
                                             <div className='h-10 w-10 rounded-full bg-blue-500 flex items-center justify-center'>
                                                <span className='text-white text-sm font-medium'>
                                                   {invitationDetail.creator.name
                                                      .charAt(0)
                                                      .toUpperCase()}
                                                </span>
                                             </div>
                                          )}
                                          <div>
                                             <p className='text-sm font-medium text-gray-900'>
                                                {invitationDetail.creator.name}
                                             </p>
                                             {invitationDetail.creator.email && (
                                                <p className='text-sm text-gray-500'>
                                                   {invitationDetail.creator.email}
                                                </p>
                                             )}
                                          </div>
                                       </div>
                                    </div>
                                 </div>
                              )}

                              {/* Invitee Information */}
                              <div>
                                 <h4 className='text-sm font-medium text-gray-900 mb-3'>
                                    Invitee Information
                                 </h4>
                                 <div className='bg-gray-50 p-4 rounded-lg space-y-3'>
                                    <div className='flex justify-between'>
                                       <span className='text-sm text-gray-500'>Email:</span>
                                       <span className='text-sm text-gray-900'>
                                          {invitationDetail.invitation.recipient_email}
                                       </span>
                                    </div>
                                    {invitationDetail.invitation.recipient_name && (
                                       <div className='flex justify-between'>
                                          <span className='text-sm text-gray-500'>Name:</span>
                                          <span className='text-sm text-gray-900'>
                                             {invitationDetail.invitation.recipient_name}
                                          </span>
                                       </div>
                                    )}
                                    {invitationDetail.acceptedBy && (
                                       <div>
                                          <span className='text-sm text-gray-500'>
                                             Accepted by:
                                          </span>
                                          <div className='mt-1'>
                                             <p className='text-sm font-medium text-gray-900'>
                                                {invitationDetail.acceptedBy.name}
                                             </p>
                                             {invitationDetail.acceptedBy.email && (
                                                <p className='text-sm text-gray-500'>
                                                   {invitationDetail.acceptedBy.email}
                                                </p>
                                             )}
                                          </div>
                                       </div>
                                    )}
                                 </div>
                              </div>
                           </div>

                           {/* Right Column - Content Details */}
                           <div className='space-y-6'>
                              {/* Album Details */}
                              {invitationDetail.album && (
                                 <div>
                                    <h4 className='text-sm font-medium text-gray-900 mb-3'>
                                       Album Details
                                    </h4>
                                    <div className='bg-gray-50 p-4 rounded-lg space-y-3'>
                                       {invitationDetail.album.coverImage && (
                                          <img
                                             className='w-full h-32 object-cover rounded-md'
                                             src={invitationDetail.album.coverImage}
                                             alt={invitationDetail.album.title}
                                          />
                                       )}
                                       <div>
                                          <h5 className='text-sm font-medium text-gray-900'>
                                             {invitationDetail.album.title}
                                          </h5>
                                          {invitationDetail.album.description && (
                                             <p className='text-sm text-gray-500 mt-1'>
                                                {invitationDetail.album.description}
                                             </p>
                                          )}
                                       </div>
                                       <div className='flex justify-between'>
                                          <span className='text-sm text-gray-500'>Created:</span>
                                          <span className='text-sm text-gray-900'>
                                             {formatDate(invitationDetail.album.createdAt)}
                                          </span>
                                       </div>
                                    </div>
                                 </div>
                              )}

                              {/* Event Details */}
                              {invitationDetail.event && (
                                 <div>
                                    <h4 className='text-sm font-medium text-gray-900 mb-3'>
                                       Event Details
                                    </h4>
                                    <div className='bg-gray-50 p-4 rounded-lg space-y-3'>
                                       {invitationDetail.event.coverImage && (
                                          <img
                                             className='w-full h-32 object-cover rounded-md'
                                             src={invitationDetail.event.coverImage}
                                             alt={invitationDetail.event.title}
                                          />
                                       )}
                                       <div>
                                          <h5 className='text-sm font-medium text-gray-900'>
                                             {invitationDetail.event.title}
                                          </h5>
                                          {invitationDetail.event.description && (
                                             <p className='text-sm text-gray-500 mt-1'>
                                                {invitationDetail.event.description}
                                             </p>
                                          )}
                                       </div>
                                       {invitationDetail.event.location && (
                                          <div className='flex justify-between'>
                                             <span className='text-sm text-gray-500'>
                                                Location:
                                             </span>
                                             <span className='text-sm text-gray-900'>
                                                {invitationDetail.event.location}
                                             </span>
                                          </div>
                                       )}
                                       {invitationDetail.event.startDate && (
                                          <div className='flex justify-between'>
                                             <span className='text-sm text-gray-500'>
                                                Start Date:
                                             </span>
                                             <span className='text-sm text-gray-900'>
                                                {formatDate(invitationDetail.event.startDate)}
                                             </span>
                                          </div>
                                       )}
                                       {invitationDetail.event.endDate && (
                                          <div className='flex justify-between'>
                                             <span className='text-sm text-gray-500'>
                                                End Date:
                                             </span>
                                             <span className='text-sm text-gray-900'>
                                                {formatDate(invitationDetail.event.endDate)}
                                             </span>
                                          </div>
                                       )}
                                    </div>
                                 </div>
                              )}

                              {/* Trip Details */}
                              {invitationDetail.trip && (
                                 <div>
                                    <h4 className='text-sm font-medium text-gray-900 mb-3'>
                                       Trip Details
                                    </h4>
                                    <div className='bg-gray-50 p-4 rounded-lg space-y-3'>
                                       {invitationDetail.trip.coverImage && (
                                          <img
                                             className='w-full h-32 object-cover rounded-md'
                                             src={invitationDetail.trip.coverImage}
                                             alt={invitationDetail.trip.title}
                                          />
                                       )}
                                       <div>
                                          <h5 className='text-sm font-medium text-gray-900'>
                                             {invitationDetail.trip.title}
                                          </h5>
                                          {invitationDetail.trip.description && (
                                             <p className='text-sm text-gray-500 mt-1'>
                                                {invitationDetail.trip.description}
                                             </p>
                                          )}
                                       </div>
                                       {invitationDetail.trip.destination && (
                                          <div className='flex justify-between'>
                                             <span className='text-sm text-gray-500'>
                                                Destination:
                                             </span>
                                             <span className='text-sm text-gray-900'>
                                                {invitationDetail.trip.destination}
                                             </span>
                                          </div>
                                       )}
                                       {invitationDetail.trip.startDate && (
                                          <div className='flex justify-between'>
                                             <span className='text-sm text-gray-500'>
                                                Start Date:
                                             </span>
                                             <span className='text-sm text-gray-900'>
                                                {formatDate(invitationDetail.trip.startDate)}
                                             </span>
                                          </div>
                                       )}
                                       {invitationDetail.trip.endDate && (
                                          <div className='flex justify-between'>
                                             <span className='text-sm text-gray-500'>
                                                End Date:
                                             </span>
                                             <span className='text-sm text-gray-900'>
                                                {formatDate(invitationDetail.trip.endDate)}
                                             </span>
                                          </div>
                                       )}
                                    </div>
                                 </div>
                              )}

                              {/* Custom Message */}
                              {(invitationDetail.invitation.personalMessage ||
                                 invitationDetail.invitation.customMessage) && (
                                 <div>
                                    <h4 className='text-sm font-medium text-gray-900 mb-3'>
                                       Message
                                    </h4>
                                    <div className='bg-gray-50 p-4 rounded-lg'>
                                       <p className='text-sm text-gray-900'>
                                          {invitationDetail.invitation.personalMessage ||
                                             invitationDetail.invitation.customMessage}
                                       </p>
                                    </div>
                                 </div>
                              )}
                           </div>
                        </div>
                     </div>

                     {/* Footer */}
                     <div className='bg-gray-50 px-6 py-3'>
                        <div className='flex justify-end'>
                           <button
                              type='button'
                              className='inline-flex justify-center rounded-md border border-transparent bg-blue-100 px-4 py-2 text-sm font-medium text-blue-900 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'
                              onClick={onClose}
                           >
                              Close
                           </button>
                        </div>
                     </div>
                  </>
               ) : (
                  <div className='p-6 text-center'>
                     <p className='text-gray-500'>Failed to load invitation details</p>
                  </div>
               )}
            </div>
         </div>
      </div>
   );
};

export default InvitationDetailModal;
